import { Card, CardContent, CardHeader, CardTitle, Label, Switch, toast } from "@devhop/ui";
import { SlidersHorizontalIcon } from "lucide-react";
import {
  SHOP_FEATURE_FLAG_KEYS,
  type ShopFeatureFlags,
} from "@/modules/shops/domain/contracts/shop-feature-flags";
import { useUpdateShopFeatureFlags } from "../api/queries";

const FLAG_LABELS: Record<keyof ShopFeatureFlags, string> = {
  inventory: "ສິນຄ້າຄົງຄັງ ແລະ ສາງ",
  warehouse_locations: "ຕຳແໜ່ງສຕ໋ອກໃນສາງ (zone / location)",
  warehouse_layout: "ແຜນຜັງຕຳແໜ່ງສາງ (layout preview / editor)",
  shifts: "ກະວຽກ POS (ບັງຄັບເປີດກະກ່ອນຂາຍ)",
  provision_sellable:
    "POS — ເພີ່ມສິນຄ້າໃໝ່ຂາຍໃນຮ້ານ (Admin ຄີ fitment ທີ່ຄິວ catalog)",
  claims: "ເຄມສິນຄ້າ (product claims)",
  procurement: "ຈັດຊື້ / PO / ຜູ້ສະໜອງ / ຮັບສິນຄ້າເຂົ້າ",
  multi_branch: "ຫຼາຍສາຂາ ແລະ ສະລັບສາຂາ",
  settlement: "Wallet / settlement / payout",
  marketplace_sync: "Marketplace — ເຜີຍແຜ່ listing ออนไลน์",
  marketplace_auto_publish: "Marketplace — ເຜີຍແຜ່ອັດຕະໂນມັດ",
  promotions: "ໂປຣໂມຊັນ / ສ່ວນຫຼຸດ POS",
  workshop: "ງານສ້ອມ / Workshop (ໃບງານ, ກະດານງານ)",
};

const FLAG_HINTS: Partial<Record<keyof ShopFeatureFlags, string>> = {
  warehouse_layout:
    "Must enable warehouse_locations first — shows the layout tab on manage warehouse positions",
  shifts:
    "ປິດ = POS ຂາຍໄດ້ໂດຍບໍ່ຕ້ອງເປີດ-ປິດກະ (ຕາມ feature flag ຮ້ານ)",
  marketplace_auto_publish:
    "fitment ຄົບ + ມີສະຕ໋ອກ → publish ອັດຕະໂນມັດ (ຕ້ອງເປີດ marketplace_sync)",
  workshop:
    "ປິດ = ເຊື່ອງເມນູງານສ້ອມໃນ POS ແລະ ບລັອກ API workshop",
};

export function ShopFeatureFlagsPanel({
  shopId,
  flags,
  canUpdate,
}: {
  shopId: string;
  flags: ShopFeatureFlags;
  canUpdate: boolean;
}) {
  const updateFlags = useUpdateShopFeatureFlags(shopId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SlidersHorizontalIcon className="size-5 text-[#004799]" />
          ຟີເຈີລະດັບຮ້ານ (feature flags)
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          ຄວບຄຸມໂມດູນທີ່ແຕກຕ່າງຈາກສິດຂອງພະນັກງານ — ປິດຈະບັງຄັບທັງຮ້ານ. ອີງຕາມ{" "}
          <span className="font-mono text-xs">IMPLEMENT_SHOP_FEATURE_FLAGS</span>
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {SHOP_FEATURE_FLAG_KEYS.map((key) => {
          const hint = FLAG_HINTS[key];
          return (
            <div
              key={key}
              className="flex flex-col gap-1.5 border-border border-b pb-4 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 space-y-0.5">
                <Label className="text-sm sm:text-base">{FLAG_LABELS[key]}</Label>
                <p className="font-mono text-[10px] text-muted-foreground sm:text-xs">
                  {key}
                </p>
                {hint ? (
                  <p className="text-muted-foreground text-xs leading-snug">
                    {hint}
                  </p>
                ) : null}
              </div>
              <Switch
                checked={flags[key]}
                disabled={!canUpdate || updateFlags.isPending}
                onCheckedChange={(v) => {
                  updateFlags.mutate(
                    { [key]: v },
                    {
                      onError: () =>
                        toast.error("ບັນທຶກ feature flag ລົ້ມເຫຼວ"),
                    },
                  );
                }}
                aria-label={FLAG_LABELS[key]}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
